import { useState, useEffect, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import * as Sentry from '@sentry/react';
import { supabase } from '@/integrations/supabase/client';

// Persist suspension reason across component instances
const SUSPENSION_STORAGE_KEY = 'momentumeq_suspended_reason';
const PASSWORD_RECOVERY_KEY = 'momentumeq_password_recovery';

const getStoredSuspension = (): string | null => {
  try {
    return sessionStorage.getItem(SUSPENSION_STORAGE_KEY);
  } catch {
    return null;
  }
};

const setStoredSuspension = (reason: string | null) => {
  try {
    if (reason) {
      sessionStorage.setItem(SUSPENSION_STORAGE_KEY, reason);
    } else {
      sessionStorage.removeItem(SUSPENSION_STORAGE_KEY);
    }
  } catch {
    // Ignore storage errors
  }
};

type SuspensionListener = (reason: string | null) => void;
let suspensionCache: string | null = getStoredSuspension();
const suspensionListeners = new Set<SuspensionListener>();

const publishSuspension = (reason: string | null) => {
  suspensionCache = reason;
  setStoredSuspension(reason);
  suspensionListeners.forEach((fn) => fn(reason));
};

const subscribeSuspension = (fn: SuspensionListener) => {
  suspensionListeners.add(fn);
  return () => {
    suspensionListeners.delete(fn);
  };
};

export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [suspendedReason, setSuspendedReasonState] = useState<string | null>(suspensionCache);
  const intentionalSignOutRef = useRef(false);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(() => {
    try {
      return sessionStorage.getItem(PASSWORD_RECOVERY_KEY) === 'true';
    } catch {
      return false;
    }
  });

  // Keep all hook instances in sync
  useEffect(() => {
    return subscribeSuspension(setSuspendedReasonState);
  }, []);

  // Wrapper to persist suspension reason
  const setSuspendedReason = useCallback((reason: string | null) => {
    publishSuspension(reason);
  }, []);

  // Check if the user's account is suspended
  const checkSuspensionStatus = useCallback(async (userId: string) => {
    const { data: profile } = await supabase
      .from('profiles')
      .select('status, suspended_reason')
      .eq('user_id', userId)
      .maybeSingle();

    if (profile?.status === 'suspended') {
      // Store reason BEFORE signing out so it persists
      const reason = profile.suspended_reason || 'Your account has been suspended.';
      setSuspendedReason(reason);
      await supabase.auth.signOut();
      return true;
    }
    
    setSuspendedReason(null);
    return false;
  }, [setSuspendedReason]);

  useEffect(() => {
    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        // Handle unexpected SIGNED_OUT — attempt session recovery
        if (event === 'SIGNED_OUT' && !intentionalSignOutRef.current) {
          setTimeout(async () => {
            try {
              const { data } = await supabase.auth.getSession();
              if (data.session) {
                setSession(data.session);
                setUser(data.session.user);
                Sentry.setUser({ id: data.session.user.id, email: data.session.user.email });
                return;
              }
            } catch {
              // Recovery failed — fall through to normal sign-out handling
            }
          }, 100);
        }
        intentionalSignOutRef.current = false;

        // Guard: skip state updates on TOKEN_REFRESHED when identity hasn't changed
        // to prevent cascading re-renders across all useAuth consumers
        setSession(prev => {
          if (event === 'TOKEN_REFRESHED' && prev?.user?.id === session?.user?.id) return prev;
          return session;
        });
        setUser(prev => {
          const newUser = session?.user ?? null;
          if (prev?.id === newUser?.id) return prev;
          return newUser;
        });
        if (event !== 'TOKEN_REFRESHED') {
          Sentry.setUser(session?.user ? { id: session.user.id, email: session.user.email } : null);
        }
        setLoading(false);

        // Detect password recovery flow
        if (event === 'PASSWORD_RECOVERY') {
          try {
            sessionStorage.setItem(PASSWORD_RECOVERY_KEY, 'true');
          } catch {
            // Ignore storage errors
          }
          setIsPasswordRecovery(true);
        }

        // Check suspension status when user signs in
        if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
          setTimeout(() => {
            checkSuspensionStatus(session.user.id);
          }, 0);

          // Update last_login_at on SIGNED_IN only (fire-and-forget)
          if (event === 'SIGNED_IN') {
            supabase
              .from('profiles')
              .update({ last_login_at: new Date().toISOString() })
              .eq('user_id', session.user.id)
              .then(() => {}); // Fire-and-forget
          }
        }
      }
    );

    // THEN check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      Sentry.setUser(session?.user ? { id: session.user.id, email: session.user.email } : null);
      setLoading(false);

      // Check suspension status for existing session
      if (session?.user) {
        setTimeout(() => {
          checkSuspensionStatus(session.user.id);
        }, 0);
      }
    });

    return () => subscription.unsubscribe();
  }, [checkSuspensionStatus]);

  // Proactive session refresh when tab becomes visible
  useEffect(() => {
    const handleVisibility = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const { data } = await supabase.auth.getSession();
        if (!data.session) return;
        const expiresAt = data.session.expires_at;
        if (expiresAt) {
          const remainingMs = expiresAt * 1000 - Date.now();
          if (remainingMs < 5 * 60 * 1000) {
            await supabase.auth.refreshSession();
          }
        }
      } catch {
        // Ignore refresh errors
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);
    return () => document.removeEventListener('visibilitychange', handleVisibility);
  }, []);

  const clearPasswordRecovery = useCallback(() => {
    try {
      sessionStorage.removeItem(PASSWORD_RECOVERY_KEY);
    } catch {
      // Ignore storage errors
    }
    setIsPasswordRecovery(false);
  }, []);

  const signUp = async (email: string, password: string) => {
    const redirectUrl = `${window.location.origin}/auth?confirmed=true`;
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl
      }
    });
    
    // Check if user needs to confirm email
    const needsEmailConfirmation = data?.user && !data?.session;
    
    return { error, needsEmailConfirmation };
  };

  const resetPassword = async (email: string) => {
    const redirectUrl = `${window.location.origin}/auth?reset=true`;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
    });
    return { error };
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({
      password: newPassword,
    });
    return { error };
  };

  const signIn = async (email: string, password: string) => {
    setSuspendedReason(null);
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });

    // Check suspension status immediately after sign in
    if (data?.user) {
      const isSuspended = await checkSuspensionStatus(data.user.id);
      if (isSuspended) {
        return { error: new Error('Your account has been suspended.') };
      }
    }

    return { error };
  };

  const signOut = async () => {
    intentionalSignOutRef.current = true;
    setSuspendedReason(null);
    const { error } = await supabase.auth.signOut();
    return { error };
  };

  const signInWithGoogle = async () => {
    setSuspendedReason(null);
    // Check for pending invite code in localStorage OR current URL
    const INVITE_CODE_STORAGE_KEY = 'momentumeq_pending_invite_code';
    const INVITE_CODE_EXPIRY_MS = 24 * 60 * 60 * 1000; // 24 hours
    let redirectUrl = `${window.location.origin}/dashboard`;
    try {
      // First check localStorage with expiry
      let pendingCode: string | null = null;
      const raw = localStorage.getItem(INVITE_CODE_STORAGE_KEY);
      if (raw) {
        const data = JSON.parse(raw);
        if (Date.now() - data.timestamp <= INVITE_CODE_EXPIRY_MS) {
          pendingCode = data.code;
        } else {
          localStorage.removeItem(INVITE_CODE_STORAGE_KEY);
        }
      }

      // If not in storage, check URL params (in case user clicks Google before storage is set)
      if (!pendingCode) {
        const urlParams = new URLSearchParams(window.location.search);
        const urlCode = urlParams.get('code');
        if (urlCode && urlCode.length >= 8 && urlCode.length <= 20) {
          pendingCode = urlCode.replace(/\s/g, '').toUpperCase();
          // Store it for post-OAuth use (with timestamp for expiry)
          localStorage.setItem(INVITE_CODE_STORAGE_KEY, JSON.stringify({ code: pendingCode, timestamp: Date.now() }));
        }
      }

      if (pendingCode) {
        // Note: OAuth callback will strip query params, so the code is stored in localStorage
        // SmartRedirect will pick it up and redirect to /auth?code=XXX
        redirectUrl = `${window.location.origin}/`;
      }
    } catch {
      // Ignore storage errors
    }
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: redirectUrl,
      }
    });
    return { error };
  };

  return {
    user,
    session,
    loading,
    suspendedReason,
    isPasswordRecovery,
    signUp,
    signIn,
    signOut,
    signInWithGoogle,
    resetPassword,
    updatePassword,
    clearPasswordRecovery,
  };
};
